import { SmartTableColumn } from '@toggle/design-system';
import { BrandAndProduct } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { SectionBase } from '~/modules/overview-knowledge-graph/components/shared/section-base/SectionBase';
import { getKnowledgeGraphSearchParams } from '~/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { useBrandsAndProducts } from '~/react-query-hooks/knowledge-graph/useBrandsAndProducts';
import { appPaths } from '~/routes/app-paths';
import { useBrandAndProductDrawer } from '~/widgets/knowledge-graph-widget/components/brands-and-products-tab/useBrandAndProductDrawer';
import { KnowledgeGraphTabType } from '~/widgets/knowledge-graph-widget/KnowledgeGraphWidget';

import styles from './BrandsAndProductsSection.module.scss';

export interface BrandsAndProductsSectionProps {
  entityTag: string;
  isInDrawer?: boolean;
}

export const BrandsAndProductsSection = ({
  entityTag,
  isInDrawer = false,
}: BrandsAndProductsSectionProps) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openDrawer } = useBrandAndProductDrawer();

  const {
    data: brandsAndProducts = [],
    isLoading,
    isError,
  } = useBrandsAndProducts(entityTag);

  if (isError) {
    return <ErrorMessageWrapper displayIcon />;
  }

  const connectionsSearchParams = getKnowledgeGraphSearchParams({
    entityTag,
    searchParams,
    subtab: KnowledgeGraphTabType.BrandsAndProducts,
  }).toString();

  const linkToConnections = `${appPaths.assetOverview}?${connectionsSearchParams}`;

  const topItems = [...brandsAndProducts]
    .sort((a, b) => a.relationship.rank - b.relationship.rank)
    .slice(0, 3)
    .map(item => ({ ...item, key: item.relationship.id }));

  const tableColumns: SmartTableColumn<BrandAndProduct>[] = [
    {
      label: t('widget:assetOverview.knowledgeGraphRevamp.columns.name'),
      key: 'product',
      render: value => <div className={styles.title}>{value}</div>,
    },
  ];

  return (
    <SectionBase
      title={t('widget:assetOverview.knowledgeGraphRevamp.brandsAndProducts')}
      tableData={topItems}
      tableColumns={tableColumns}
      isFetching={isLoading}
      onExpand={() => navigate(linkToConnections)}
      onRowClick={item =>
        isInDrawer ? navigate(linkToConnections) : openDrawer(item.product)
      }
    />
  );
};
